import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { useCompartilhamentos, CompartilhadaBadge } from './CompartilhadaBadge';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ModernPageHeader, ModernStatCard } from '../ui/ModernComponents';
import MultaTransitoModal from './MultaTransitoModal';
import { 
  Plus, 
  DollarSign, 
  AlertTriangle,
  CheckCircle,
  User,
  Bike,
  Calendar,
  Search,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  RefreshCw,
  FileText,
  Phone,
  Filter,
  AlertCircle,
  Clock,
  ExternalLink,
  CreditCard,
  FileDown,
  Minus,
  Wallet,
  X
} from 'lucide-react';
import { apiService } from '../../services/api';

// Multas padrão pré-cadastradas
const MULTAS_PADRAO = [
  { id: 'multa_leve', nome: 'Multa Leve', valor: 130.16, categoria: 'multa' },
  { id: 'multa_media', nome: 'Multa Média', valor: 193.23, categoria: 'multa' },
  { id: 'multa_grave', nome: 'Multa Grave', valor: 293.47, categoria: 'multa' },
  { id: 'multa_gravissima', nome: 'Multa Gravíssima', valor: 880.41, categoria: 'multa' },
];

// Tipos de cobrança (valores devem corresponder ao enum do backend)
const TIPOS_COBRANCA = {
  'multa_transito': { label: 'Multa de Trânsito', color: 'bg-red-500/20 text-red-300 border border-red-500/30', icon: '🚨' },
  'dano_moto': { label: 'Dano ao Veículo', color: 'bg-orange-500/20 text-orange-300 border border-orange-500/30', icon: '🔧' },
  'reposicao_peca': { label: 'Reposição de Peça', color: 'bg-blue-500/20 text-blue-300 border border-blue-500/30', icon: '⚙️' },
  'franquia_sinistro': { label: 'Franquia Sinistro', color: 'bg-purple-500/20 text-purple-300 border border-purple-500/30', icon: '🚗' },
  'mal_uso': { label: 'Mal Uso', color: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30', icon: '⚠️' },
  'outros': { label: 'Outros', color: 'bg-slate-500/20 text-slate-200 border border-slate-500/30', icon: '📋' },
};

export const DebitosPage = () => {
  const compartilhamentos = useCompartilhamentos();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [debitos, setDebitos] = useState([]);
  const [locacoes, setLocacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [expandedLocacoes, setExpandedLocacoes] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingDebito, setEditingDebito] = useState(null);
  const [selectedLocacao, setSelectedLocacao] = useState(null);
  // Trava de concorrência: id do débito sendo quitado/abatido (impede double-click)
  const [debitoProcessando, setDebitoProcessando] = useState(null);
  
  // Modal de abatimento
  const [showAbatimentoModal, setShowAbatimentoModal] = useState(false);
  const [debitoAbatimento, setDebitoAbatimento] = useState(null);
  const [abatimentoForm, setAbatimentoForm] = useState({
    valor: '',
    data_pagamento: new Date().toISOString().split('T')[0],
    forma_pagamento: 'dinheiro',
    observacoes: ''
  });
  
  // Modal de abatimento total
  const [showAbatimentoTotalModal, setShowAbatimentoTotalModal] = useState(false);
  const [locacaoAbatimentoTotal, setLocacaoAbatimentoTotal] = useState(null);
  const [abatimentoTotalForm, setAbatimentoTotalForm] = useState({
    valor: '',
    observacoes: ''
  });

  // Modal de parcelamento TOTAL (todos os débitos da locação)
  const [showParcelarTotalModal, setShowParcelarTotalModal] = useState(false);
  const [locacaoParcelarTotal, setLocacaoParcelarTotal] = useState(null);
  const [parcelarTotalForm, setParcelarTotalForm] = useState({
    modo: 'parcelado',
    parcelas_total: 4,
  });
  const [salvandoParcelarTotal, setSalvandoParcelarTotal] = useState(false);

  const abrirParcelarTotal = (locacao, totalPendente, qtdDebitos) => {
    setLocacaoParcelarTotal({
      id: locacao.id,
      placa: locacao.moto?.placa,
      cliente: locacao.cliente?.nome,
      totalPendente,
      qtdDebitos,
    });
    setParcelarTotalForm({ modo: 'parcelado', parcelas_total: 4 });
    setShowParcelarTotalModal(true);
  };

  const salvarParcelarTotal = async () => {
    if (!locacaoParcelarTotal) return;
    if (parcelarTotalForm.modo === 'parcelado') {
      const n = parseInt(parcelarTotalForm.parcelas_total);
      if (!n || n < 1) {
        alert('Informe a quantidade de parcelas (mínimo 1).');
        return;
      }
    }
    setSalvandoParcelarTotal(true);
    try {
      const body = parcelarTotalForm.modo === 'parcelado'
        ? { modo: 'parcelado', parcelas_total: parseInt(parcelarTotalForm.parcelas_total) }
        : { modo: 'avista' };
      const r = await apiService.api.put(
        `/cobrancas-extras/locacao/${locacaoParcelarTotal.id}/parcelamento-total`,
        body
      );
      const d = r.data;
      const linha = d.modo === 'parcelado'
        ? `${d.qtd_debitos} débito(s) parcelados em ${d.parcelas_total}x. Cliente vai pagar R$ ${Number(d.parcela_semanal).toFixed(2)} por semana até quitar tudo (R$ ${Number(d.total_restante).toFixed(2)}).`
        : `${d.qtd_debitos} débito(s) marcados como À VISTA. Saldo total: R$ ${Number(d.total_restante).toFixed(2)}.`;
      alert(`✅ Aplicado!\n\n${linha}\n\nLembretes e pagamentos foram sincronizados.`);
      setShowParcelarTotalModal(false);
      await carregarDados();
    } catch (e) {
      alert('Erro ao aplicar parcelamento: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSalvandoParcelarTotal(false);
    }
  };

  
  // Modal de confirmação para redividir parcelas
  const [showRedividirModal, setShowRedividirModal] = useState(false);
  const [redividirInfo, setRedividirInfo] = useState(null);
  const [novoValorParcela, setNovoValorParcela] = useState('');
  
  // Valores customizados salvos
  const [valoresCustomizados, setValoresCustomizados] = useState([]);
  const [novoValorNome, setNovoValorNome] = useState('');
  const [novoValorPreco, setNovoValorPreco] = useState('');
  
  // Seleção de valores pré-definidos
  const [valorSelecionado, setValorSelecionado] = useState(null);
  const [showMultaModal, setShowMultaModal] = useState(false);
  
  const [formData, setFormData] = useState({
    locacao_id: '',
    tipo: 'multa_transito',
    descricao: '',
    valor: '',
    data_ocorrencia: new Date().toISOString().split('T')[0],
    data_vencimento: '',
    observacoes: '',
    status: 'pendente'
  });

  useEffect(() => {
    carregarDados();
    // Carregar valores customizados do localStorage
    const saved = localStorage.getItem('valores_cobranca_custom');
    if (saved) {
      setValoresCustomizados(JSON.parse(saved));
    }
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [debitosData, locacoesData] = await Promise.all([
        apiService.getCobrancasExtras(),
        apiService.getLocacoes()
      ]);
      
      setDebitos(debitosData || []);
      // Incluir locações ativas E locações com débitos pendentes (mesmo finalizadas)
      const locacoesAtivas = (locacoesData || []).filter(l => l.status === 'ativa');
      const idsLocacoesAtivas = locacoesAtivas.map(l => l.id);
      
      // Verificar se há débitos pendentes de locações não ativas
      const idsLocacoesComDebitosPendentes = [...new Set(
        (debitosData || [])
          .filter(d => d.status === 'pendente')
          .map(d => d.locacao_id)
          .filter(id => id && !idsLocacoesAtivas.includes(id))
      )];
      
      // Buscar locações finalizadas que têm débitos pendentes
      const locacoesComDebitosPendentes = (locacoesData || []).filter(l => 
        idsLocacoesComDebitosPendentes.includes(l.id)
      );
      
      // Combinar todas as locações relevantes
      setLocacoes([...locacoesAtivas, ...locacoesComDebitosPendentes]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Agrupa débitos por locação
  const debitosPorLocacao = useMemo(() => {
    const agrupado = {};
    
    locacoes.forEach(locacao => {
      const locacaoDebitos = debitos.filter(d => 
        d.locacao_id === locacao.id
      );
      
      const totalPendente = locacaoDebitos
        .filter(d => d.status === 'pendente' || d.status === 'parcial')
        .reduce((acc, d) => {
          const valor = parseFloat(d.valor) || 0;
          const pago = parseFloat(d.valor_pago) || 0;
          const abatido = parseFloat(d.valor_abatido) || 0;
          const restante = d.valor_restante != null ? parseFloat(d.valor_restante) : (valor - Math.max(pago, abatido));
          return acc + Math.max(0, restante);
        }, 0);
      
      const totalPago = locacaoDebitos
        .reduce((acc, d) => acc + Math.max(parseFloat(d.valor_pago) || 0, parseFloat(d.valor_abatido) || 0), 0);
      
      agrupado[locacao.id] = {
        locacao,
        debitos: locacaoDebitos.sort((a, b) => new Date(b.data_ocorrencia) - new Date(a.data_ocorrencia)),
        totalPendente,
        totalPago,
        totalDebitos: locacaoDebitos.length,
        temPendentes: totalPendente > 0
      };
    });
    
    return agrupado;
  }, [locacoes, debitos]);

  // Filtra locações
  const locacoesFiltradas = useMemo(() => {
    return Object.values(debitosPorLocacao).filter(({ locacao, temPendentes }) => {
      const matchSearch = !searchTerm || 
        locacao.moto_placa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        locacao.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const isExLocatario = locacao.status !== 'ativa';
      
      const matchStatus = filterStatus === 'todos' || 
        (filterStatus === 'pendentes' && temPendentes && !isExLocatario) ||
        (filterStatus === 'ex_locatarios' && isExLocatario && temPendentes) ||
        (filterStatus === 'sem_debitos' && !temPendentes);
      
      return matchSearch && matchStatus;
    }).sort((a, b) => b.totalPendente - a.totalPendente);
  }, [debitosPorLocacao, searchTerm, filterStatus]);

  // Estatísticas gerais
  const estatisticas = useMemo(() => {
    const totalPendente = debitos
      .filter(d => d.status === 'pendente' || d.status === 'parcial')
      .reduce((acc, d) => {
        const valor = parseFloat(d.valor) || 0;
        const pago = parseFloat(d.valor_pago) || 0;
        const abatido = parseFloat(d.valor_abatido) || 0;
        const restante = d.valor_restante != null ? parseFloat(d.valor_restante) : (valor - Math.max(pago, abatido));
        return acc + Math.max(0, restante);
      }, 0);
    
    const totalPago = debitos
      .reduce((acc, d) => acc + Math.max(parseFloat(d.valor_pago) || 0, parseFloat(d.valor_abatido) || 0), 0);
    
    const locacoesComDebito = Object.values(debitosPorLocacao).filter(l => l.temPendentes).length;
    
    const exLocatariosComDebito = Object.values(debitosPorLocacao).filter(l => l.temPendentes && l.locacao.status !== 'ativa').length;
    
    return { totalPendente, totalPago, totalDebitos: debitos.length, locacoesComDebito, exLocatariosComDebito };
  }, [debitos, debitosPorLocacao]);

  const toggleExpand = (locacaoId) => {
    setExpandedLocacoes(prev => ({
      ...prev,
      [locacaoId]: !prev[locacaoId]
    }));
  };

  const abrirModalNovo = (locacao = null) => {
    console.log('Abrindo modal para locação:', locacao);
    setEditingDebito(null);
    setSelectedLocacao(locacao);
    setValorSelecionado(null);
    setFormData({
      locacao_id: locacao?.id || '',
      tipo: 'multa_transito',
      descricao: '',
      valor: '',
      data_ocorrencia: new Date().toISOString().split('T')[0],
      data_vencimento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      observacoes: '',
      status: 'pendente'
    });
    setShowModal(true);
  };

  const baixarComprovante = async (debitoId) => {
    try {
      const response = await apiService.api.get(`/cobrancas-extras/${debitoId}/comprovante`, {
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprovante_debito_${debitoId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar comprovante:', error);
      alert('Erro ao gerar comprovante. Tente novamente.');
    }
  };

  // Funções de Abatimento
  const abrirModalAbatimento = (debito) => {
    setDebitoAbatimento(debito);
    setAbatimentoForm({
      valor: '',
      data_pagamento: new Date().toISOString().split('T')[0],
      forma_pagamento: 'dinheiro',
      observacoes: ''
    });
    setShowAbatimentoModal(true);
  };

  const handleAbatimento = async () => {
    if (!debitoAbatimento || !abatimentoForm.valor) {
      alert('Informe o valor do abatimento');
      return;
    }

    const valorAbatimento = parseFloat(abatimentoForm.valor);
    if (isNaN(valorAbatimento) || valorAbatimento <= 0) {
      alert('Valor inválido');
      return;
    }

    // Calcular valor restante após abatimento
    const valorAtual = debitoAbatimento.valor_restante || debitoAbatimento.valor;
    const valorRestante = valorAtual - valorAbatimento;

    try {
      // Registrar o abatimento
      await apiService.api.post(`/cobrancas-extras/${debitoAbatimento.id}/abatimento`, {
        valor_abatimento: valorAbatimento,
        data_pagamento: abatimentoForm.data_pagamento,
        forma_pagamento: abatimentoForm.forma_pagamento,
        observacoes: abatimentoForm.observacoes
      });

      alert(`Abatimento de ${formatarMoeda(valorAbatimento)} registrado com sucesso!${valorRestante <= 0 ? ' Débito quitado!' : ''}`);
      setShowAbatimentoModal(false);
      setDebitoAbatimento(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao registrar abatimento:', error);
      alert('Erro ao registrar abatimento: ' + (error.response?.data?.detail || error.message));
    }
  };

  const abrirAbatimentoTotal = (locacaoId, locacaoInfo) => {
    setLocacaoAbatimentoTotal({ id: locacaoId, ...locacaoInfo });
    setAbatimentoTotalForm({ valor: '', observacoes: '' });
    setShowAbatimentoTotalModal(true);
  };

  const confirmarAbatimentoTotal = async () => {
    const valorAbatimento = parseFloat(abatimentoTotalForm.valor);
    if (!valorAbatimento || valorAbatimento <= 0) {
      alert('Por favor, informe um valor válido para o abatimento.');
      return;
    }

    try {
      // Primeiro, registrar o abatimento
      const resultado = await apiService.abaterTotalDebitos({
        locacao_id: locacaoAbatimentoTotal.id,
        valor_abatimento: valorAbatimento,
        observacoes: abatimentoTotalForm.observacoes
      });

      // Calcular se o saldo restante pode ser redividido
      const saldoRestante = (locacaoAbatimentoTotal.totalPendente || 0) - valorAbatimento;
      
      if (saldoRestante > 0) {
        // Calcular valor sugerido para nova parcela (dividido em 4)
        const parcelaSugerida = Math.round(saldoRestante / 4 * 100) / 100;
        
        // Mostrar modal perguntando se quer redividir
        setRedividirInfo({
          locacaoId: locacaoAbatimentoTotal.id,
          cliente: locacaoAbatimentoTotal.cliente,
          placa: locacaoAbatimentoTotal.placa,
          valorAbatido: valorAbatimento,
          saldoRestante: saldoRestante,
          parcelaSugerida: parcelaSugerida
        });
        setNovoValorParcela(parcelaSugerida.toFixed(2));
        setShowAbatimentoTotalModal(false);
        setShowRedividirModal(true);
      } else {
        alert(`✅ ${resultado.mensagem} - Todos os débitos foram quitados!`);
        setShowAbatimentoTotalModal(false);
        setLocacaoAbatimentoTotal(null);
        carregarDados();
      }
    } catch (error) {
      console.error('Erro ao registrar abatimento total:', error);
      alert('Erro ao registrar abatimento: ' + (error.response?.data?.detail || error.message));
    }
  };

  const confirmarRedividir = async (redividir) => {
    if (!redividir) {
      // Não redividir - apenas fechar e atualizar
      alert(`✅ Abatimento de ${formatarMoeda(redividirInfo.valorAbatido)} registrado! Saldo restante: ${formatarMoeda(redividirInfo.saldoRestante)}`);
      setShowRedividirModal(false);
      setRedividirInfo(null);
      carregarDados();
      return;
    }

    // Redividir com o valor da parcela definido
    const valorParcela = parseFloat(novoValorParcela);
    if (!valorParcela || valorParcela <= 0) {
      alert('Por favor, informe um valor válido para a parcela.');
      return;
    }

    try {
      // Salvar a nova configuração de parcela
      await apiService.atualizarParcelamento(redividirInfo.locacaoId, {
        valor_parcela: valorParcela,
        saldo_restante: redividirInfo.saldoRestante,
        redividido: true,
        observacoes: `Saldo de ${formatarMoeda(redividirInfo.saldoRestante)} redividido em parcelas de ${formatarMoeda(valorParcela)}`
      });

      const numParcelas = Math.ceil(redividirInfo.saldoRestante / valorParcela);
      alert(`✅ Saldo de ${formatarMoeda(redividirInfo.saldoRestante)} redividido em ${numParcelas}x de ${formatarMoeda(valorParcela)}`);
      
      setShowRedividirModal(false);
      setRedividirInfo(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao redividir parcelas:', error);
      alert('Erro ao redividir: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Modal de modo de cobrança (À vista / Parcelado)
  const [showModoCobrancaModal, setShowModoCobrancaModal] = useState(false);
  const [debitoModoCobranca, setDebitoModoCobranca] = useState(null);
  const [modoCobrancaForm, setModoCobrancaForm] = useState({
    modo: 'avista',
    parcelas_total: 4,
    valor_parcela_fixa: ''
  });
  const [salvandoModoCobranca, setSalvandoModoCobranca] = useState(false);

  // Modal de Histórico de Abatimentos
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [historicoDebito, setHistoricoDebito] = useState(null);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [reconciliando, setReconciliando] = useState(false);

  const abrirHistoricoAbatimentos = async (debito) => {
    setHistoricoDebito({ ...debito, _carregando: true });
    setShowHistoricoModal(true);
    setHistoricoLoading(true);
    try {
      const r = await apiService.api.get(`/cobrancas-extras/${debito.id}/historico-abatimentos`);
      setHistoricoDebito(r.data);
    } catch (e) {
      setHistoricoDebito({ erro: e.response?.data?.detail || e.message });
    } finally {
      setHistoricoLoading(false);
    }
  };

  const reconciliarDebito = async () => {
    if (!historicoDebito?.cobranca_id) return;
    setReconciliando(true);
    try {
      await apiService.api.post(`/cobrancas-extras/${historicoDebito.cobranca_id}/reconciliar`);
      // Recarregar histórico
      const r = await apiService.api.get(`/cobrancas-extras/${historicoDebito.cobranca_id}/historico-abatimentos`);
      setHistoricoDebito(r.data);
      // Recarregar lista de débitos
      await carregarDados();
      alert('Débito reconciliado com sucesso! Pagamentos e lembretes atualizados.');
    } catch (e) {
      alert('Erro ao reconciliar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setReconciliando(false);
    }
  };

  const abrirModalModoCobranca = (debito) => {
    const restante = debito.valor_restante ?? debito.valor;
    setDebitoModoCobranca(debito);
    setModoCobrancaForm({
      modo: debito.modo_cobranca || 'avista',
      parcelas_total: debito.parcelas_total || 4,
      valor_parcela_fixa: debito.valor_parcela_fixa || ''
    });
    setShowModoCobrancaModal(true);
  };

  const salvarModoCobranca = async () => {
    if (modoCobrancaForm.modo === 'parcelado') {
      const n = parseInt(modoCobrancaForm.parcelas_total);
      if (!n || n < 1) {
        alert('Informe a quantidade de parcelas (mínimo 1).');
        return;
      }
    }
    setSalvandoModoCobranca(true);
    try {
      const payload = { modo: modoCobrancaForm.modo };
      if (modoCobrancaForm.modo === 'parcelado') {
        payload.parcelas_total = parseInt(modoCobrancaForm.parcelas_total);
        if (modoCobrancaForm.valor_parcela_fixa !== '' && modoCobrancaForm.valor_parcela_fixa !== null) {
          payload.valor_parcela_fixa = parseFloat(modoCobrancaForm.valor_parcela_fixa);
        }
      }
      await apiService.api.put(`/cobrancas-extras/${debitoModoCobranca.id}/modo-cobranca`, payload);
      setShowModoCobrancaModal(false);
      await carregarDados();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSalvandoModoCobranca(false);
    }
  };

  const abrirModalEditar = (debito) => {
    setEditingDebito(debito);
    setSelectedLocacao(null);
    setValorSelecionado(null);
    setFormData({
      locacao_id: debito.locacao_id || '',
      tipo: debito.tipo || 'multa_transito',
      descricao: debito.descricao || '',
      valor: debito.valor || '',
      data_ocorrencia: debito.data_ocorrencia?.split('T')[0] || '',
      data_vencimento: debito.data_vencimento?.split('T')[0] || '',
      observacoes: debito.observacoes || '',
      status: debito.status || 'pendente'
    });
    setShowModal(true);
  };

  const selecionarValorPredefinido = (item) => {
    setValorSelecionado(item);
    setFormData(prev => ({
      ...prev,
      valor: item.valor.toString(),
      descricao: item.nome
    }));
  };

  const adicionarValorCustomizado = () => {
    if (!novoValorNome.trim() || !novoValorPreco) return;
    
    const novoValor = {
      id: `custom_${Date.now()}`,
      nome: novoValorNome.trim(),
      valor: parseFloat(novoValorPreco),
      categoria: 'custom',
      custom: true
    };
    
    const novosCustom = [...valoresCustomizados, novoValor];
    setValoresCustomizados(novosCustom);
    localStorage.setItem('valores_cobranca_custom', JSON.stringify(novosCustom));
    
    // Seleciona automaticamente
    selecionarValorPredefinido(novoValor);
    
    setNovoValorNome('');
    setNovoValorPreco('');
  };

  const removerValorCustomizado = (valorId) => {
    const novosCustom = valoresCustomizados.filter(v => v.id !== valorId);
    setValoresCustomizados(novosCustom);
    localStorage.setItem('valores_cobranca_custom', JSON.stringify(novosCustom));
  };

  const salvarDebito = async () => {
    try {
      // Buscar dados da locação para preencher cliente e moto
      const locacao = locacoes.find(l => l.id === formData.locacao_id) || selectedLocacao;
      
      if (!locacao) {
        alert('Selecione uma locação');
        return;
      }
      
      const dados = {
        locacao_id: locacao.id,
        cliente_id: locacao.cliente_id || locacao.id,
        moto_id: locacao.moto_id || locacao.id,
        tipo: formData.tipo,
        titulo: formData.descricao || 'Débito',
        descricao: formData.descricao || 'Débito',
        valor: parseFloat(formData.valor) || 0,
        data_ocorrencia: formData.data_ocorrencia,
        data_vencimento: formData.data_vencimento || formData.data_ocorrencia,
        observacoes: formData.observacoes || '',
        evidencias: []
      };
      
      if (editingDebito) {
        await apiService.atualizarCobrancaExtra(editingDebito.id, {
          status: formData.status,
          observacoes: formData.observacoes
        });
      } else {
        await apiService.criarCobrancaExtra(dados);
      }
      
      setShowModal(false);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar débito:', error);
      alert('Erro ao salvar débito: ' + (error.response?.data?.detail || error.message));
    }
  };

  const marcarComoPago = async (debito) => {
    // Trava anti-duplo-clique: se já está processando este débito, ignora
    if (debitoProcessando === debito.id) return;
    const valorRestante = Number(
      debito.valor_restante !== null && debito.valor_restante !== undefined
        ? debito.valor_restante
        : (Number(debito.valor || 0) - Number(debito.valor_pago || 0))
    );
    if (valorRestante <= 0.005) {
      // Débito já está quitado
      if (debito.status !== 'pago') {
        try {
          setDebitoProcessando(debito.id);
          await apiService.api.post(`/cobrancas-extras/${debito.id}/reconciliar`);
          await carregarDados();
        } catch (e) { /* já está quitado, segue */ }
        finally { setDebitoProcessando(null); }
      }
      return;
    }

    const titulo = debito.titulo || debito.descricao || 'Débito';
    if (!window.confirm(`Quitar "${titulo}" recebendo ${formatarMoeda(valorRestante)} agora?`)) return;

    setDebitoProcessando(debito.id);
    try {
      await apiService.api.post(`/cobrancas-extras/${debito.id}/abatimento`, {
        valor_abatimento: valorRestante,
        forma_pagamento: 'dinheiro',
        observacoes: 'Quitação manual via "Marcar como Pago"',
      });
      await carregarDados();
    } catch (error) {
      console.error('Erro ao marcar como pago:', error);
      alert('Erro ao marcar como pago: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDebitoProcessando(null);
    }
  };

  const excluirDebito = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este débito?')) return;
    
    try {
      await apiService.deletarCobrancaExtra(id);
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir débito:', error);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const abrirWhatsApp = (locacao, debito = null) => {
    const telefone = locacao.cliente_telefone?.replace(/\D/g, '');
    if (!telefone) {
      alert('Telefone não encontrado');
      return;
    }
    
    let mensagem = `Olá ${locacao.cliente_nome},\n\n`;
    
    if (debito) {
      mensagem += `Informamos que existe um débito pendente referente à moto ${locacao.moto_placa}:\n\n`;
      mensagem += `📋 *${debito.descricao}*\n`;
      mensagem += `💰 Valor: *${formatarMoeda(debito.valor)}*\n`;
      mensagem += `📅 Vencimento: ${formatarData(debito.data_vencimento)}\n\n`;
    } else {
      const pendentes = debitosPorLocacao[locacao.id]?.debitos.filter(d => d.status === 'pendente') || [];
      const total = pendentes.reduce((acc, d) => acc + (parseFloat(d.valor) || 0), 0);
      
      mensagem += `Informamos que existem débitos pendentes referentes à moto ${locacao.moto_placa}:\n\n`;
      pendentes.forEach(d => {
        mensagem += `• ${d.descricao}: ${formatarMoeda(d.valor)}\n`;
      });
      mensagem += `\n💰 *Total: ${formatarMoeda(total)}*\n\n`;
    }
    
    mensagem += `Por favor, entre em contato para regularização.\n\nDesloca Motos 🏍️`;
    
    window.open(`https://wa.me/55${telefone}?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">Carregando débitos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-screen pb-20" data-testid="debitos-page">
      {/* Header Moderno */}
      <ModernPageHeader
        title="Débitos e Cobranças"
        subtitle="Multas e valores em aberto por locatário"
        icon={AlertTriangle}
        gradient="from-red-900 to-slate-900"
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={carregarDados} variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-xl">
              <RefreshCw className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            <Button
              onClick={() => !isSubAdmin && setShowMultaModal(true)}
              disabled={isSubAdmin}
              data-testid="btn-nova-multa-transito"
              className={`bg-gradient-to-r from-orange-600 via-red-600 to-red-700 hover:from-orange-500 hover:to-red-600 rounded-xl shadow-lg shadow-red-500/30 ${isSubAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Multa de trânsito com anexo de PDF/imagem — notifica o cliente"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Nova Multa de Trânsito</span>
              <span className="sm:hidden">Multa</span>
            </Button>
            <Button onClick={() => !isSubAdmin && abrirModalNovo()} disabled={isSubAdmin} className={`bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 rounded-xl ${isSubAdmin ? "opacity-50 cursor-not-allowed" : ""}`}>
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Outros Débitos</span>
              <span className="sm:hidden">Débito</span>
            </Button>
          </div>
        }
      />

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ModernStatCard
          icon={AlertTriangle}
          value={formatarMoeda(estatisticas.totalPendente).replace('R$', '').trim()}
          label="Total Pendente"
          gradient="from-red-600 to-red-800"
          suffix=""
        />
        <ModernStatCard
          icon={CheckCircle}
          value={formatarMoeda(estatisticas.totalPago).replace('R$', '').trim()}
          label="Total Pago"
          gradient="from-green-600 to-emerald-800"
          suffix=""
        />
        <ModernStatCard
          icon={User}
          value={estatisticas.locacoesComDebito}
          label="Com Débitos"
          gradient="from-yellow-600 to-orange-700"
        />
        <ModernStatCard
          icon={FileText}
          value={estatisticas.totalDebitos}
          label="Total Registros"
          gradient="from-blue-600 to-blue-800"
        />
      </div>

      {/* Filtros */}
      <Card className="bg-slate-800/80 border border-white/10">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar placa ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm bg-slate-700/50 border-white/10 text-white placeholder:text-gray-400"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-300" />
              <select
                data-testid="filter-debitos"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-slate-700/50 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-medium"
              >
                <option value="todos">Todos</option>
                <option value="pendentes">Locatários Ativos</option>
                <option value="ex_locatarios">Ex-Locatários ({estatisticas.exLocatariosComDebito})</option>
                <option value="sem_debitos">Sem débitos</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Locações com Débitos */}
      <div className="space-y-4">
        {locacoesFiltradas.length === 0 ? (
          <Card className="bg-slate-800">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma locação encontrada</p>
            </CardContent>
          </Card>
        ) : (
          locacoesFiltradas.map(({ locacao, debitos: locacaoDebitos, totalPendente, totalPago, totalDebitos, temPendentes }) => (
            <Card key={locacao.id} className={`bg-slate-800/90 border border-white/10 overflow-hidden ${locacao.status !== 'ativa' ? 'border-l-4 border-l-amber-500' : temPendentes ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-emerald-500'}`}>
              {/* Header do Locatário */}
              <div 
                className="p-4 cursor-pointer hover:bg-slate-700/50 transition-colors"
                onClick={() => toggleExpand(locacao.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${locacao.status !== 'ativa' ? 'bg-amber-500/20 border border-amber-500/30' : temPendentes ? 'bg-red-500/20 border border-red-500/30' : 'bg-emerald-500/20 border border-emerald-500/30'}`}>
                      <Bike className={`w-5 h-5 md:w-6 md:h-6 ${locacao.status !== 'ativa' ? 'text-amber-400' : temPendentes ? 'text-red-400' : 'text-emerald-400'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-base md:text-lg text-white flex items-center gap-1.5 flex-wrap">
                          {locacao.moto_placa}
                          <CompartilhadaBadge nomes={compartilhamentos.por_locacao[locacao.id] || compartilhamentos.por_placa[locacao.moto_placa]} />
                        </h3>
                        {locacao.status !== 'ativa' && (
                          <span data-testid={`badge-ex-locatario-${locacao.id}`} className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase">
                            Ex-Locatário
                          </span>
                        )}
                      </div>
                      <p className="text-xs md:text-sm text-gray-300 truncate flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {locacao.cliente_nome}
                        {locacao.status !== 'ativa' && (
                          <span className="text-amber-400/70 text-[10px] ml-1">({locacao.status})</span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 md:gap-4">
                    {/* Info mobile */}
                    <div className="flex md:hidden flex-col items-end text-xs">
                      {temPendentes ? (
                        <>
                          <span className="font-bold text-red-400">{formatarMoeda(totalPendente)}</span>
                          <span className="text-gray-300">pendente</span>
                        </>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-semibold">Em dia</Badge>
                      )}
                    </div>
                    
                    {/* Info desktop */}
                    <div className="hidden md:flex items-center gap-4 text-sm">
                      {temPendentes && (
                        <div className="text-center px-3 py-1 bg-red-500/10 rounded-lg border border-red-500/20">
                          <p className="font-bold text-lg text-red-400">{formatarMoeda(totalPendente)}</p>
                          <p className="text-red-300/80 text-xs font-medium">Pendente</p>
                        </div>
                      )}
                      <div className="text-center px-3 py-1 bg-slate-700/50 rounded-lg">
                        <p className="font-bold text-lg text-white">{totalDebitos}</p>
                        <p className="text-gray-300 text-xs font-medium">Débitos</p>
                      </div>
                      {!temPendentes && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-semibold">Em dia</Badge>
                      )}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirModalNovo(locacao);
                      }}
                      className="hidden sm:flex text-red-400 hover:text-red-300 hover:bg-red-500/10 font-medium"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Débito
                    </Button>
                    
                    {temPendentes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirWhatsApp(locacao);
                        }}
                        className="hidden sm:flex text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 font-medium"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Cobrar
                      </Button>
                    )}
                    
                    {temPendentes && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirParcelarTotal(locacao, totalPendente, locacaoDebitos.filter(d => d.status === 'pendente' || d.status === 'parcial').length);
                        }}
                        className="hidden sm:flex text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 font-medium"
                        data-testid={`btn-parcelar-total-${locacao.id}`}
                        title="Aplicar o mesmo modo de cobrança em TODOS os débitos desta locação"
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Parcelar Total
                      </Button>
                    )}

                    {temPendentes && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirAbatimentoTotal(locacao.id, { placa: locacao.moto?.placa, cliente: locacao.cliente?.nome, totalPendente });
                        }}
                        className="hidden sm:flex text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 font-medium"
                      >
                        <Wallet className="w-4 h-4 mr-1" />
                        Abater Total
                      </Button>
                    )}
                    
                    {expandedLocacoes[locacao.id] ? (
                      <ChevronUp className="w-5 h-5 text-gray-300" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-300" />
                    )}
                  </div>
                </div>
                
                {/* Botões mobile */}
                <div className="flex sm:hidden gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirModalNovo(locacao);
                    }}
                    className="flex-1 text-red-400 border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-xs font-medium"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Débito
                  </Button>
                  {temPendentes && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirWhatsApp(locacao);
                      }}
                      className="flex-1 text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-medium"
                    >
                      <Phone className="w-3 h-3 mr-1" />
                      Cobrar
                    </Button>
                  )}
                  {temPendentes && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirParcelarTotal(locacao, totalPendente, locacaoDebitos.filter(d => d.status === 'pendente' || d.status === 'parcial').length);
                      }}
                      className="flex-1 text-blue-400 border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-xs font-medium"
                      data-testid={`btn-parcelar-total-mobile-${locacao.id}`}
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      Parcelar
                    </Button>
                  )}
                  {temPendentes && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirAbatimentoTotal(locacao.id, { placa: locacao.moto?.placa, cliente: locacao.cliente?.nome, totalPendente });
                      }}
                      className="flex-1 text-purple-400 border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-xs font-medium"
                    >
                      <Wallet className="w-3 h-3 mr-1" />
                      Abater
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Lista de Débitos Expandida */}
              {expandedLocacoes[locacao.id] && (
                <div className="bg-slate-900/50 border-t border-white/10">
                  {locacaoDebitos.length === 0 ? (
                    <div className="p-6 text-center">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                      <p className="text-gray-300 font-medium">Nenhum débito registrado</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/10">
                      {locacaoDebitos.map((debito, index) => {
                        const tipoInfo = TIPOS_COBRANCA[debito.tipo] || TIPOS_COBRANCA['outros'];
                        const isPago = debito.status === 'pago';
                        
                        return (
                          <div 
                            key={debito.id || index} 
                            className={`p-4 ${isPago ? 'bg-slate-800/30 opacity-70' : 'bg-slate-800/60'}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={`${tipoInfo.color} text-xs font-semibold`}>
                                  {tipoInfo.icon} {tipoInfo.label}
                                </Badge>
                                {isPago && (
                                  <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-semibold">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Pago
                                  </Badge>
                                )}
                                {!isPago && debito.modo_cobranca === 'parcelado' && (
                                  <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-xs font-semibold">
                                    Parcelado: {debito.parcelas_pagas || 0}/{debito.parcelas_total || '?'} ·
                                    R$ {Number(debito.valor_parcela_fixa || 0).toFixed(2)}/parc
                                  </Badge>
                                )}
                                {!isPago && !debito.modo_cobranca && (
                                  <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30 text-xs font-semibold" title="Débito legado — usa o parcelamento antigo da locação se houver. Clique no calendário para definir.">
                                    Modo automático
                                  </Badge>
                                )}
                                {!isPago && debito.modo_cobranca === 'avista' && (
                                  <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30 text-xs font-semibold">
                                    À vista
                                  </Badge>
                                )}
                              </div>
                              <div className="flex gap-1">
                                {!isPago && !isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => marcarComoPago(debito)}
                                    disabled={debitoProcessando === debito.id}
                                    className="p-1 h-8 w-8 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                                    title="Marcar como pago"
                                    data-testid={`btn-quitar-debito-${debito.id}`}
                                  >
                                    {debitoProcessando === debito.id ? (
                                      <span className="block w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4" />
                                    )}
                                  </Button>
                                )}
                                {!isPago && !isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirModalAbatimento(debito)}
                                    className="p-1 h-8 w-8 text-yellow-400 hover:bg-yellow-500/10"
                                    title="Registrar Abatimento"
                                  >
                                    <Wallet className="w-4 h-4" />
                                  </Button>
                                )}
                                {isPago && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => baixarComprovante(debito.id)}
                                    className="p-1 h-8 w-8 text-purple-400 hover:bg-purple-500/10"
                                    title="Baixar Comprovante de Quitação"
                                  >
                                    <FileDown className="w-4 h-4" />
                                  </Button>
                                )}
                                {!isPago && !isSubAdmin && (
                                  <Button
                                    data-testid={`btn-modo-cobranca-${debito.id}`}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirModalModoCobranca(debito)}
                                    className="p-1 h-8 w-8 text-cyan-400 hover:bg-cyan-500/10"
                                    title="Configurar cobrança (à vista / parcelado)"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  data-testid={`btn-historico-abatimentos-${debito.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => abrirHistoricoAbatimentos(debito)}
                                  className="p-1 h-8 w-8 text-purple-400 hover:bg-purple-500/10"
                                  title="Histórico de abatimentos"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirModalEditar(debito)}
                                    className="p-1 h-8 w-8 text-blue-400 hover:bg-blue-500/10"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                                {!isSubAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => excluirDebito(debito.id)}
                                    className="p-1 h-8 w-8 text-red-400 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            <p className="text-white font-semibold mb-2 text-base">
                              {debito.descricao || 'Sem descrição'}
                            </p>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center gap-1.5 text-gray-200">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-medium">{formatarData(debito.data_ocorrencia)}</span>
                              </div>
                              <div className={`flex items-center gap-1.5 font-bold text-base ${isPago ? 'text-gray-400' : 'text-red-400'}`}>
                                <DollarSign className="w-3.5 h-3.5" />
                                {formatarMoeda(debito.valor_restante || debito.valor)}
                                {debito.valor_abatido > 0 && (
                                  <span className="text-xs text-yellow-400 ml-1">
                                    (-{formatarMoeda(debito.valor_abatido)})
                                  </span>
                                )}
                              </div>
                              {debito.data_vencimento && (
                                <div className="flex items-center gap-1.5 text-gray-200">
                                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="font-medium">Venc: {formatarData(debito.data_vencimento)}</span>
                                </div>
                              )}
                            </div>
                            
                            {debito.observacoes && (
                              <p className="text-sm text-gray-300 mt-2 italic bg-slate-700/30 p-2 rounded">
                                {debito.observacoes}
                              </p>
                            )}
                            
                            {/* Botão cobrar individual */}
                            {!isPago && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => abrirWhatsApp(locacao, debito)}
                                className="mt-3 text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs w-full sm:w-auto font-semibold"
                              >
                                <Phone className="w-3 h-3 mr-1" />
                                Enviar Cobrança WhatsApp
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Modal de Novo/Editar Débito */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-rose-400" />
              {editingDebito ? 'Editar Débito' : 'Novo Débito'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Seleção de Locação */}
            {!selectedLocacao && !editingDebito && (
              <div>
                <Label>Locatário/Moto *</Label>
                <select
                  value={formData.locacao_id}
                  onChange={(e) => setFormData({ ...formData, locacao_id: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  required
                >
                  <option value="">Selecione...</option>
                  {locacoes.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.moto_placa} - {loc.cliente_nome}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {selectedLocacao && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="font-medium">{selectedLocacao.moto_placa}</p>
                <p className="text-sm text-gray-300">{selectedLocacao.cliente_nome}</p>
              </div>
            )}
            
            {/* Tipo */}
            <div>
              <Label>Tipo *</Label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                className="w-full border rounded-md px-3 py-2 mt-1"
              >
                {Object.entries(TIPOS_COBRANCA).map(([key, { label, icon }]) => (
                  <option key={key} value={key}>{icon} {label}</option>
                ))}
              </select>
            </div>
            
            {/* Valores Pré-definidos de Multas */}
            <div>
              <Label>Valores de Multa</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {MULTAS_PADRAO.map(multa => (
                  <button
                    key={multa.id}
                    type="button"
                    onClick={() => selecionarValorPredefinido(multa)}
                    className={`p-2 rounded-lg border-2 text-left transition-all text-sm ${
                      valorSelecionado?.id === multa.id 
                        ? 'border-red-500 bg-red-50 text-rose-300' 
                        : 'border-white/10 hover:border-red-300'
                    }`}
                  >
                    <div className="font-medium">{multa.nome}</div>
                    <div className="text-gray-300">R$ {multa.valor.toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Valores Customizados */}
            {valoresCustomizados.length > 0 && (
              <div>
                <Label>Meus Valores</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {valoresCustomizados.map(valor => (
                    <div key={valor.id} className="relative">
                      <button
                        type="button"
                        onClick={() => selecionarValorPredefinido(valor)}
                        className={`w-full p-2 rounded-lg border-2 text-left transition-all text-sm ${
                          valorSelecionado?.id === valor.id 
                            ? 'border-blue-500 bg-blue-50 text-blue-300' 
                            : 'border-white/10 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{valor.nome}</div>
                        <div className="text-gray-300 text-xs">R$ {valor.valor.toFixed(2)}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => removerValorCustomizado(valor.id)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Adicionar Novo Valor */}
            <div className="border-t pt-4">
              <Label>Adicionar Novo Valor</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Nome/Descrição"
                  value={novoValorNome}
                  onChange={(e) => setNovoValorNome(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Valor"
                  value={novoValorPreco}
                  onChange={(e) => setNovoValorPreco(e.target.value)}
                  className="w-28"
                  step="0.01"
                />
                <Button
                  type="button"
                  onClick={adicionarValorCustomizado}
                  disabled={!novoValorNome.trim() || !novoValorPreco}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  +
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Valores adicionados ficam salvos para futuras cobranças
              </p>
            </div>
            
            {/* Descrição e Valor Manual */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Descrição *</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Ex: Multa por excesso de velocidade"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder="0,00"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Data Ocorrência</Label>
                <Input
                  type="date"
                  value={formData.data_ocorrencia}
                  onChange={(e) => setFormData({ ...formData, data_ocorrencia: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Data Vencimento</Label>
              <Input
                type="date"
                value={formData.data_vencimento}
                onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações adicionais..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            {/* Status (apenas edição) */}
            {editingDebito && (
              <div>
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 mt-1"
                >
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                </select>
              </div>
            )}
            
            {/* Resumo */}
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-200">Valor do Débito:</span>
                <span className="text-xl font-bold text-rose-400">
                  {formatarMoeda(parseFloat(formData.valor) || 0)}
                </span>
              </div>
            </div>
            
            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={salvarDebito}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={!formData.descricao || !formData.valor || (!formData.locacao_id && !selectedLocacao)}
              >
                {editingDebito ? 'Salvar Alterações' : 'Registrar Débito'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Abatimento */}
      <Dialog open={showAbatimentoModal} onOpenChange={setShowAbatimentoModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-400">
              <Wallet className="w-5 h-5" />
              Registrar Abatimento
            </DialogTitle>
          </DialogHeader>
          
          {debitoAbatimento && (
            <div className="space-y-4">
              {/* Info do Débito */}
              <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
                <p className="text-sm text-gray-400">Débito selecionado:</p>
                <p className="font-semibold text-white">{debitoAbatimento.descricao || debitoAbatimento.titulo}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Valor original:</span>
                  <span className="text-white">{formatarMoeda(debitoAbatimento.valor)}</span>
                </div>
                {debitoAbatimento.valor_abatido > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Já abatido:</span>
                    <span className="text-yellow-400">-{formatarMoeda(debitoAbatimento.valor_abatido)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-slate-600 pt-2">
                  <span className="text-gray-400 font-semibold">Valor restante:</span>
                  <span className="text-red-400 font-bold">{formatarMoeda(debitoAbatimento.valor_restante || debitoAbatimento.valor)}</span>
                </div>
              </div>

              {/* Valor do Abatimento */}
              <div>
                <Label className="text-gray-300">Valor do Abatimento (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={abatimentoForm.valor}
                  onChange={(e) => setAbatimentoForm({...abatimentoForm, valor: e.target.value})}
                  placeholder="0,00"
                  className="mt-1 bg-slate-700 border-slate-600 text-white"
                />
                {/* Botões de valor rápido */}
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAbatimentoForm({...abatimentoForm, valor: ((debitoAbatimento.valor_restante || debitoAbatimento.valor) * 0.25).toFixed(2)})}
                    className="text-xs"
                  >
                    25%
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAbatimentoForm({...abatimentoForm, valor: ((debitoAbatimento.valor_restante || debitoAbatimento.valor) * 0.50).toFixed(2)})}
                    className="text-xs"
                  >
                    50%
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAbatimentoForm({...abatimentoForm, valor: (debitoAbatimento.valor_restante || debitoAbatimento.valor).toFixed(2)})}
                    className="text-xs"
                  >
                    Total
                  </Button>
                </div>
              </div>

              {/* Data do Pagamento */}
              <div>
                <Label className="text-gray-300">Data do Pagamento</Label>
                <Input
                  type="date"
                  value={abatimentoForm.data_pagamento}
                  onChange={(e) => setAbatimentoForm({...abatimentoForm, data_pagamento: e.target.value})}
                  className="mt-1 bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Forma de Pagamento */}
              <div>
                <Label className="text-gray-300">Forma de Pagamento</Label>
                <select
                  value={abatimentoForm.forma_pagamento}
                  onChange={(e) => setAbatimentoForm({...abatimentoForm, forma_pagamento: e.target.value})}
                  className="mt-1 w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2"
                >
                  <option value="dinheiro">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="cartao_debito">Cartão de Débito</option>
                  <option value="cartao_credito">Cartão de Crédito</option>
                  <option value="transferencia">Transferência</option>
                </select>
              </div>

              {/* Observações */}
              <div>
                <Label className="text-gray-300">Observações</Label>
                <Textarea
                  value={abatimentoForm.observacoes}
                  onChange={(e) => setAbatimentoForm({...abatimentoForm, observacoes: e.target.value})}
                  placeholder="Observações sobre o pagamento..."
                  className="mt-1 bg-slate-700 border-slate-600 text-white"
                  rows={2}
                />
              </div>

              {/* Resumo */}
              {abatimentoForm.valor && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-400">Valor do abatimento:</span>
                    <span className="text-xl font-bold text-yellow-400">
                      {formatarMoeda(parseFloat(abatimentoForm.valor) || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1 text-sm">
                    <span className="text-gray-400">Restará:</span>
                    <span className={`font-semibold ${((debitoAbatimento.valor_restante || debitoAbatimento.valor) - parseFloat(abatimentoForm.valor)) <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatarMoeda(Math.max(0, (debitoAbatimento.valor_restante || debitoAbatimento.valor) - (parseFloat(abatimentoForm.valor) || 0)))}
                      {((debitoAbatimento.valor_restante || debitoAbatimento.valor) - parseFloat(abatimentoForm.valor)) <= 0 && ' (Quitado!)'}
                    </span>
                  </div>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAbatimentoModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAbatimento}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                  disabled={!abatimentoForm.valor || parseFloat(abatimentoForm.valor) <= 0}
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Registrar Abatimento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Abatimento Total */}
      <Dialog open={showAbatimentoTotalModal} onOpenChange={setShowAbatimentoTotalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-400">
              <Wallet className="h-5 w-5" />
              Abater do Total de Débitos
            </DialogTitle>
          </DialogHeader>
          
          {locacaoAbatimentoTotal && (
            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-purple-300 text-sm">
                  Este valor será distribuído automaticamente entre os débitos pendentes, 
                  do mais antigo ao mais recente.
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-lg p-4 space-y-2">
                <p className="text-gray-300"><strong>Cliente:</strong> {locacaoAbatimentoTotal.cliente}</p>
                <p className="text-gray-300"><strong>Placa:</strong> {locacaoAbatimentoTotal.placa}</p>
                <p className="text-lg font-bold text-orange-400">
                  Total Pendente: {formatarMoeda(locacaoAbatimentoTotal.totalPendente || 0)}
                </p>
              </div>
              
              <div>
                <Label htmlFor="valorAbatimentoTotal" className="text-gray-300">Valor do Abatimento (R$)</Label>
                <Input
                  id="valorAbatimentoTotal"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={abatimentoTotalForm.valor}
                  onChange={(e) => setAbatimentoTotalForm(prev => ({ ...prev, valor: e.target.value }))}
                  placeholder="0,00"
                  className="text-lg bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="observacoesAbatimentoTotal" className="text-gray-300">Observações (opcional)</Label>
                <Textarea
                  id="observacoesAbatimentoTotal"
                  value={abatimentoTotalForm.observacoes}
                  onChange={(e) => setAbatimentoTotalForm(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Ex: Pagamento PIX recebido"
                  rows={2}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAbatimentoTotalModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmarAbatimentoTotal}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={!abatimentoTotalForm.valor || parseFloat(abatimentoTotalForm.valor) <= 0}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmar Abatimento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Parcelar Total (todos os débitos da locação) */}
      <Dialog open={showParcelarTotalModal} onOpenChange={setShowParcelarTotalModal}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-400">
              <Calendar className="h-5 w-5" />
              Parcelar Total dos Débitos
            </DialogTitle>
          </DialogHeader>

          {locacaoParcelarTotal && (
            <div className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm">
                <p className="text-blue-200">
                  Aplica o <strong>mesmo modo de cobrança</strong> a todos os <strong>{locacaoParcelarTotal.qtdDebitos}</strong> débito(s) pendentes desta locação. O lembrete e o pagamento semanal serão atualizados automaticamente.
                </p>
              </div>

              <div className="bg-slate-800 rounded-lg p-4 space-y-1 text-sm">
                <p className="text-gray-300"><strong>Cliente:</strong> {locacaoParcelarTotal.cliente}</p>
                <p className="text-gray-300"><strong>Placa:</strong> {locacaoParcelarTotal.placa}</p>
                <p className="text-orange-400 font-bold text-base mt-2">
                  Total Pendente: {formatarMoeda(locacaoParcelarTotal.totalPendente || 0)}
                </p>
              </div>

              <div>
                <Label className="text-gray-300">Modo de cobrança</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant={parcelarTotalForm.modo === 'avista' ? 'default' : 'outline'}
                    onClick={() => setParcelarTotalForm({ ...parcelarTotalForm, modo: 'avista' })}
                    className={parcelarTotalForm.modo === 'avista' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid="btn-modo-avista-total"
                  >
                    À vista
                  </Button>
                  <Button
                    type="button"
                    variant={parcelarTotalForm.modo === 'parcelado' ? 'default' : 'outline'}
                    onClick={() => setParcelarTotalForm({ ...parcelarTotalForm, modo: 'parcelado' })}
                    className={parcelarTotalForm.modo === 'parcelado' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid="btn-modo-parcelado-total"
                  >
                    Parcelado
                  </Button>
                </div>
              </div>

              {parcelarTotalForm.modo === 'parcelado' && (
                <div>
                  <Label htmlFor="parcelas_total_input" className="text-gray-300">Em quantas parcelas?</Label>
                  <Input
                    id="parcelas_total_input"
                    type="number"
                    min="1"
                    max="52"
                    value={parcelarTotalForm.parcelas_total}
                    onChange={(e) => setParcelarTotalForm({ ...parcelarTotalForm, parcelas_total: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                    data-testid="input-parcelas-total"
                  />
                  {parseInt(parcelarTotalForm.parcelas_total) > 0 && locacaoParcelarTotal.totalPendente > 0 && (
                    <p className="text-xs text-blue-300 mt-2">
                      Cliente vai pagar <strong>{formatarMoeda(locacaoParcelarTotal.totalPendente / parseInt(parcelarTotalForm.parcelas_total))}</strong> por semana até quitar tudo.
                    </p>
                  )}
                </div>
              )}

              {parcelarTotalForm.modo === 'avista' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
                  Cada débito vai cobrar o saldo INTEGRAL na próxima cobrança semanal.
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowParcelarTotalModal(false)}
                  className="flex-1"
                  disabled={salvandoParcelarTotal}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={salvarParcelarTotal}
                  disabled={salvandoParcelarTotal}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="btn-confirmar-parcelar-total"
                >
                  {salvandoParcelarTotal ? 'Aplicando...' : 'Aplicar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={showRedividirModal} onOpenChange={setShowRedividirModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-400">
              <AlertCircle className="h-5 w-5" />
              Redividir Saldo em Novas Parcelas?
            </DialogTitle>
          </DialogHeader>
          
          {redividirInfo && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-300 font-medium">
                  ✅ Abatimento de {formatarMoeda(redividirInfo.valorAbatido)} registrado com sucesso!
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-lg p-4 space-y-2">
                <p className="text-gray-300"><strong>Cliente:</strong> {redividirInfo.cliente}</p>
                <p className="text-gray-300"><strong>Placa:</strong> {redividirInfo.placa}</p>
                <p className="text-xl font-bold text-orange-400">
                  Saldo Restante: {formatarMoeda(redividirInfo.saldoRestante)}
                </p>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-300 text-sm mb-3">
                  Deseja redividir o saldo restante em novas parcelas?
                </p>
                <p className="text-gray-300 text-xs">
                  Se <strong>SIM</strong>: O saldo será dividido em novas parcelas (você pode ajustar o valor).
                </p>
                <p className="text-gray-300 text-xs mt-1">
                  Se <strong>NÃO</strong>: As parcelas continuam com os valores anteriores.
                </p>
              </div>
              
              <div className="bg-slate-800 rounded-lg p-4">
                <Label htmlFor="novoValorParcela" className="text-gray-300 mb-2 block">
                  Valor de cada nova parcela (R$)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="novoValorParcela"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={novoValorParcela}
                    onChange={(e) => setNovoValorParcela(e.target.value)}
                    className="flex-1 text-lg bg-slate-700 border-slate-600 text-white"
                  />
                  <span className="text-gray-400 text-sm whitespace-nowrap">
                    = {Math.ceil(redividirInfo.saldoRestante / (parseFloat(novoValorParcela) || 1))}x parcelas
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Sugestão: {formatarMoeda(redividirInfo.parcelaSugerida)} (dividido em 4x)
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => confirmarRedividir(false)}
                  className="flex-1 border-gray-500 text-gray-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Não Redividir
                </Button>
                <Button
                  onClick={() => confirmarRedividir(true)}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                  disabled={!novoValorParcela || parseFloat(novoValorParcela) <= 0}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Sim, Redividir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Configurar modo de cobrança (à vista / parcelado) */}
      <Dialog open={showModoCobrancaModal} onOpenChange={setShowModoCobrancaModal}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Configurar cobrança do débito
            </DialogTitle>
          </DialogHeader>
          {debitoModoCobranca && (
            <div className="space-y-4">
              <div className="rounded border border-slate-700 bg-slate-800/40 p-3 text-sm">
                <div className="text-gray-300">{debitoModoCobranca.descricao || debitoModoCobranca.titulo || 'Débito'}</div>
                <div className="text-xs text-gray-400 mt-1">
                  Saldo restante: <strong className="text-amber-300">
                    R$ {Number(debitoModoCobranca.valor_restante ?? debitoModoCobranca.valor ?? 0).toFixed(2)}
                  </strong>
                </div>
              </div>

              <div>
                <Label className="text-gray-200">Como cobrar este débito nos lembretes semanais?</Label>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    data-testid="opt-modo-avista"
                    onClick={() => setModoCobrancaForm({ ...modoCobrancaForm, modo: 'avista' })}
                    className={`flex-1 px-3 py-2 rounded border text-sm ${
                      modoCobrancaForm.modo === 'avista'
                        ? 'bg-cyan-600 border-cyan-500 text-white'
                        : 'bg-slate-800 border-slate-700 text-gray-300 hover:bg-slate-700'
                    }`}
                  >
                    À vista
                    <div className="text-xs opacity-80 mt-0.5">Saldo total no próximo lembrete</div>
                  </button>
                  <button
                    type="button"
                    data-testid="opt-modo-parcelado"
                    onClick={() => setModoCobrancaForm({ ...modoCobrancaForm, modo: 'parcelado' })}
                    className={`flex-1 px-3 py-2 rounded border text-sm ${
                      modoCobrancaForm.modo === 'parcelado'
                        ? 'bg-cyan-600 border-cyan-500 text-white'
                        : 'bg-slate-800 border-slate-700 text-gray-300 hover:bg-slate-700'
                    }`}
                  >
                    Parcelado
                    <div className="text-xs opacity-80 mt-0.5">Em N parcelas fixas</div>
                  </button>
                </div>
              </div>

              {modoCobrancaForm.modo === 'parcelado' && (
                <>
                  <div>
                    <Label className="text-gray-200">Quantas parcelas?</Label>
                    <Input
                      data-testid="input-parcelas-total"
                      type="number"
                      min="1"
                      value={modoCobrancaForm.parcelas_total}
                      onChange={(e) => setModoCobrancaForm({ ...modoCobrancaForm, parcelas_total: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-200">Valor de cada parcela (R$)</Label>
                    <Input
                      data-testid="input-valor-parcela-fixa"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder={`Deixe em branco para usar saldo/parcelas (R$ ${(
                        Number(debitoModoCobranca.valor_restante ?? debitoModoCobranca.valor ?? 0) /
                        Math.max(1, parseInt(modoCobrancaForm.parcelas_total || 1))
                      ).toFixed(2)})`}
                      value={modoCobrancaForm.valor_parcela_fixa}
                      onChange={(e) => setModoCobrancaForm({ ...modoCobrancaForm, valor_parcela_fixa: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      ⚠️ Este valor é fixo. O sistema NÃO redivide automaticamente — se o cliente pagar a mais ou menos,
                      as próximas parcelas continuam com o mesmo valor até o débito ser quitado.
                    </p>
                  </div>
                </>
              )}

              {modoCobrancaForm.modo === 'avista' && (
                <p className="text-xs text-gray-400">
                  O <strong>saldo total restante</strong> deste débito será incluído no próximo lembrete de pagamento semanal
                  do cliente, junto com o aluguel.
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowModoCobrancaModal(false)}
                  className="flex-1"
                  data-testid="btn-cancelar-modo"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={salvarModoCobranca}
                  disabled={salvandoModoCobranca}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  data-testid="btn-salvar-modo"
                >
                  {salvandoModoCobranca ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Histórico de Abatimentos */}
      <Dialog open={showHistoricoModal} onOpenChange={setShowHistoricoModal}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Histórico de Abatimentos
            </DialogTitle>
          </DialogHeader>
          {historicoLoading && (
            <p className="text-sm text-gray-400 italic">Carregando histórico...</p>
          )}
          {!historicoLoading && historicoDebito?.erro && (
            <p className="text-sm text-red-400">Erro: {historicoDebito.erro}</p>
          )}
          {!historicoLoading && historicoDebito && !historicoDebito.erro && (
            <div className="space-y-4">
              {/* Header com totais + status */}
              <div className="rounded border border-slate-700 bg-slate-800/40 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-gray-200 font-medium">{historicoDebito.titulo}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {historicoDebito.modo_cobranca === 'parcelado' && (
                      <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px]">
                        Parcelado {historicoDebito.parcelas_pagas || 0}/{historicoDebito.parcelas_total || 0}
                      </Badge>
                    )}
                    {historicoDebito.modo_cobranca === 'avista' && (
                      <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[10px]">
                        À vista
                      </Badge>
                    )}
                    {historicoDebito.status === 'pago' && (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px]">
                        Quitado
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-gray-400">Valor original</div>
                    <div className="text-white font-semibold" data-testid="historico-valor-original">
                      R$ {Number(historicoDebito.valor_original || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Total abatido</div>
                    <div className="text-emerald-300 font-semibold" data-testid="historico-total-abatido">
                      R$ {Number(historicoDebito.total_abatido || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Saldo restante</div>
                    <div className="text-amber-300 font-semibold" data-testid="historico-saldo-restante">
                      R$ {Number(historicoDebito.valor_restante || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Aviso de inconsistência */}
              {historicoDebito.inconsistente && (
                <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 font-bold">⚠</span>
                    <div className="flex-1">
                      <div className="text-red-300 font-medium">Inconsistência detectada</div>
                      <div className="text-red-200 text-xs mt-1">
                        Saldo no banco (R$ {Number(historicoDebito.valor_restante_db || 0).toFixed(2)})
                        difere do calculado pelo histórico (R$ {Number(historicoDebito.valor_restante || 0).toFixed(2)}).
                        Reconcilie para corrigir os pagamentos e lembretes.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {historicoDebito.abatimentos?.length ? (
                <ol className="relative border-l border-slate-700 ml-3 space-y-4">
                  {historicoDebito.abatimentos.map((ev, idx) => {
                    const isQuitacao = ev.tipo_recebimento === 'quitacao_debito';
                    const isSemanal = !!ev.pagamento_id || (ev.forma_pagamento || '').toLowerCase().includes('semanal');
                    const dataStr = ev.data
                      ? new Date(ev.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                      : '—';
                    return (
                      <li key={idx} className="ml-4" data-testid={`abatimento-evento-${idx}`}>
                        <span className={`absolute w-3 h-3 rounded-full -left-[7px] mt-1 ${
                          isQuitacao ? 'bg-emerald-500' : isSemanal ? 'bg-blue-500' : 'bg-purple-500'
                        }`}></span>
                        <div className="flex flex-wrap justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white font-semibold flex items-center gap-2 flex-wrap">
                              {dataStr}
                              {isQuitacao && (
                                <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px]">
                                  Quitação final
                                </Badge>
                              )}
                              {isSemanal && !isQuitacao && (
                                <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[10px]">
                                  Pagamento semanal
                                </Badge>
                              )}
                              {!isSemanal && !isQuitacao && (
                                <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px]">
                                  Abatimento direto
                                </Badge>
                              )}
                              {ev.parcela_numero && ev.parcelas_total && (
                                <Badge className="bg-slate-700 text-slate-200 border border-slate-600 text-[10px]">
                                  Parcela {ev.parcela_numero}/{ev.parcelas_total}
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                              <div>Forma: {ev.forma_pagamento || '—'}</div>
                              {ev.registrado_por && (
                                <div>Registrado por: <span className="text-gray-300">{ev.registrado_por}</span></div>
                              )}
                              {ev.observacoes && (
                                <div className="italic text-gray-500">{ev.observacoes}</div>
                              )}
                            </div>
                          </div>
                          <div className="text-lg font-bold text-emerald-300 whitespace-nowrap">
                            R$ {Number(ev.valor || 0).toFixed(2)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Nenhum abatimento registrado para este débito ainda.
                </p>
              )}

              {/* Ações */}
              <div className="flex justify-between items-center pt-2 gap-2 flex-wrap">
                <Button
                  onClick={reconciliarDebito}
                  disabled={reconciliando}
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-700"
                  data-testid="btn-reconciliar-debito"
                  title="Recalcula o saldo a partir do histórico real e sincroniza pagamentos/lembretes"
                >
                  {reconciliando ? 'Reconciliando...' : 'Reconciliar com histórico'}
                </Button>
                <Button
                  onClick={() => setShowHistoricoModal(false)}
                  className="bg-slate-700 hover:bg-slate-600"
                  data-testid="btn-fechar-historico"
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🚨 Modal de Multa de Trânsito (dedicado, com anexo PDF/IMG) */}
      {showMultaModal && (
        <MultaTransitoModal
          locacoes={locacoes.map(l => ({
            id: l.id,
            cliente_nome: l.cliente_nome,
            cliente_id: l.cliente_id,
            moto_placa: l.moto_placa,
          }))}
          onClose={() => setShowMultaModal(false)}
          onSucesso={() => {
            setShowMultaModal(false);
            carregarDados();
          }}
        />
      )}
    </div>
  );
};

export default DebitosPage;
